/* @jsx h */
import { defineComponent, h } from "vue";
import type { ProductNutritionalInformation } from "@lemmonhq/types";

type SummaryItem = {
  key: "energy" | "sugar" | "fat" | "salt";
  label: string;
  value: string;
  percentText: string;
  background: string;
  textColor: string;
};

const formatNumber = (value: number, decimals = 0) =>
  Number.isFinite(value) ? value.toFixed(decimals) : "—";

const RDA = {
  calories: 2000, // kcal
  sugar: 30, // g
  fat: 70, // g
  salt: 6, // g
};

const colors: Record<SummaryItem["key"], { bg: string; text: string }> = {
  energy: { bg: "#14532d", text: "#f8fafc" }, // dark green
  sugar: { bg: "#22c55e", text: "#f8fafc" }, // light green
  fat: { bg: "#facc15", text: "#1f2937" }, // yellow
  salt: { bg: "#ef4444", text: "#f8fafc" }, // red
};

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

const toSalt = (sodiumMilligrams?: number) => {
  if (!isFiniteNumber(sodiumMilligrams)) return undefined;
  // sodium to salt conversion factor ≈ 2.5 (mg → g)
  return ((sodiumMilligrams || 0) * 2.5) / 1000;
};

const percentOf = (value: number | undefined, target: number) => {
  if (!isFiniteNumber(value) || !target) return undefined;
  return Math.min(100, (value / target) * 100);
};

export interface NutritionalSummaryProps {
  nutrition: ProductNutritionalInformation;
}

export const NutritionalSummary = defineComponent<NutritionalSummaryProps>({
  name: "NutritionalSummary",
  props: ["nutrition"],
  setup(props) {
    const nutrition = props.nutrition;

    const sugar = nutrition.macronutrients.totalCarbohydrate.totalSugars;
    const fat = nutrition.macronutrients.totalFat.value;
    const salt = toSalt(nutrition.macronutrients.sodium?.value);

    const items: SummaryItem[] = [
      {
        key: "energy",
        label: "Energy",
        value: isFiniteNumber(nutrition.calories)
          ? `${formatNumber(nutrition.calories)} kcal`
          : "—",
        percentText: (() => {
          const pct = percentOf(nutrition.calories, RDA.calories);
          return isFiniteNumber(pct) ? `${formatNumber(pct!, 0)}% RDA` : "—";
        })(),
        background: colors.energy.bg,
        textColor: colors.energy.text,
      },
      {
        key: "sugar",
        label: "Sugars",
        value: isFiniteNumber(sugar) ? `${formatNumber(sugar!, 1)} g` : "—",
        percentText: (() => {
          const pct = percentOf(sugar, RDA.sugar);
          return isFiniteNumber(pct) ? `${formatNumber(pct!, 0)}% RDA` : "—";
        })(),
        background: colors.sugar.bg,
        textColor: colors.sugar.text,
      },
      {
        key: "fat",
        label: "Fat",
        value: isFiniteNumber(fat) ? `${formatNumber(fat, 1)} g` : "—",
        percentText: (() => {
          const pct = percentOf(fat, RDA.fat);
          return isFiniteNumber(pct) ? `${formatNumber(pct!, 0)}% RDA` : "—";
        })(),
        background: colors.fat.bg,
        textColor: colors.fat.text,
      },
      {
        key: "salt",
        label: "Salt",
        value: isFiniteNumber(salt) ? `${formatNumber(salt!, 2)} g` : "—",
        percentText: (() => {
          const pct = percentOf(salt, RDA.salt);
          return isFiniteNumber(pct) ? `${formatNumber(pct!, 0)}% RDA` : "—";
        })(),
        background: colors.salt.bg,
        textColor: colors.salt.text,
      },
    ];

    const hasData = items.some(
      (item) => item.value !== "—" && item.percentText !== "—",
    );

    if (!hasData) {
      return () => null;
    }

    return () => (
      <div class="flex flex-wrap gap-2">
        {items.map((item, i) => {
          const val = parseFloat(item.value);
          if (!Number.isFinite(val)) {
            return null;
          }
          return (
            <div
              key={i}
              class="w-[90px]  rounded-full px-4 py-2 flex flex-col items-center shadow-md"
              style={{
                backgroundColor:
                  item.value === "—" ? "#f1f5f9" : item.background,
                color: item.value === "—" ? "#475569" : item.textColor,
              }}
            >
              <span class="text-[11px] uppercase tracking-wide font-semibold opacity-90">
                {item.label}
              </span>
              <span class="text-lg font-bold leading-tight">
                {parseFloat(item.value).toFixed(2)}
              </span>
              <span class="text-[10px] font-medium opacity-80">
                {item.percentText}
              </span>
            </div>
          );
        })}
      </div>
    );
  },
});
